// filepath: /Users/ephraim/yesodcopilot/client/src/components/group/CreateMembershipModal.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faTimes, faChevronLeft, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import '../../styles/group/CreateEventModal.css'; // Reusing existing styles
import groupService from '../../api/groupService';

const CreateMembershipModal = ({ isOpen, onClose, groupId, onMembershipCreated, onMembershipUpdated, membership = null, isEditMode = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    coverImage: null,
    title: '',
    description: '',
    price: '',
    interval: 'monthly', // 'monthly', 'annually', or 'one-time'
    benefits: ['', '', ''], // Array of up to 3 reward descriptions
    thankYouNote: '', // Thank you note to members
    featured: false // Whether this is a featured membership
  });
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const totalSteps = 2;

  // Initialize form data with membership details if in edit mode
  useEffect(() => {
    if (isEditMode && membership) {
      // Parse benefits - handle both string and array formats
      let parsedBenefits = ['', '', ''];
      if (membership.benefits) {
        try {
          // If benefits is a string, parse it as JSON
          if (typeof membership.benefits === 'string') {
            const parsed = JSON.parse(membership.benefits);
            parsedBenefits = Array.isArray(parsed) ? parsed : ['', '', ''];
          } 
          // If benefits is already an array, use it directly
          else if (Array.isArray(membership.benefits)) {
            parsedBenefits = membership.benefits;
          }
          
          // Ensure we always have exactly 3 elements for the form
          while (parsedBenefits.length < 3) {
            parsedBenefits.push('');
          }
          parsedBenefits = parsedBenefits.slice(0, 3); // Keep only first 3
        } catch (error) {
          console.error('Error parsing benefits:', error);
          parsedBenefits = ['', '', ''];
        }
      }

      setFormData({
        title: membership.name || '',
        description: membership.description || '',
        price: membership.price || '',
        interval: membership.recurring === false ? 'one-time' : (membership.interval || 'monthly'),
        benefits: parsedBenefits,
        thankYouNote: membership.thankYouNote || '',
        featured: membership.featured || false,
        coverImage: null // We can't upload the existing image directly, but we can show the preview
      });

      // Set cover image preview if available
      if (membership.cover_image_url) {
        setCoverImagePreview(membership.cover_image_url);
      }
    }
  }, [isEditMode, membership]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleBenefitChange = (index, value) => {
    const updatedBenefits = [...formData.benefits];
    updatedBenefits[index] = value;
    setFormData({
      ...formData,
      benefits: updatedBenefits
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file');
        return;
      }
      
      console.log('File selected:', {
        name: file.name,
        type: file.type,
        size: `${Math.round(file.size / 1024)} KB`
      });
      
      setFormData({
        ...formData,
        coverImage: file
      });
      
      // Create preview URL for the image
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setCoverImagePreview(fileReader.result);
        console.log('Preview generated successfully');
      };
      fileReader.onerror = (error) => {
        console.error('Error creating file preview:', error);
        setError('Failed to preview the image');
      };
      fileReader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      coverImage: null,
      title: '',
      description: '',
      price: '',
      interval: 'monthly',
      benefits: ['', '', ''],
      thankYouNote: '',
      featured: false
    });
    setCoverImagePreview(null);
    setFormStep(1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Filter out empty benefits
      const filteredBenefits = formData.benefits.filter(benefit => benefit.trim() !== '');
      
      // Create partnership data
      const membershipData = new FormData();
      membershipData.append('name', formData.title);
      membershipData.append('description', formData.description);
      membershipData.append('price', formData.price);
      
      // Handle recurring and interval logic properly
      const isOneTime = formData.interval === 'one-time';
      membershipData.append('recurring', !isOneTime); // Set recurring based on interval
      
      // Only set interval if it's not one-time
      if (!isOneTime) {
        membershipData.append('interval', formData.interval);
      }
      // Don't append interval at all for one-time memberships - let server set it to null
      
      membershipData.append('featured', formData.featured);
      membershipData.append('benefits', JSON.stringify(filteredBenefits));
      membershipData.append('thankYouNote', formData.thankYouNote);
      
      // Add the cover image if available
      if (formData.coverImage) {
        // Prepare a clean filename by removing special characters
        const cleanFileName = formData.coverImage.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        console.log('Uploading cover image:', {
          originalName: formData.coverImage.name,
          cleanName: cleanFileName,
          type: formData.coverImage.type,
          size: `${Math.round(formData.coverImage.size / 1024)} KB`
        });
        
        membershipData.append('coverImage', formData.coverImage, cleanFileName);
      } else if (coverImagePreview && coverImagePreview.startsWith('data:')) {
        // If we have a preview from canvas/cropper but not a file object
        console.log('Using base64 image data from preview');
        membershipData.append('coverImage', coverImagePreview);
      }

      let response;
      
      if (isEditMode && membership) {
        // Update existing partnership
        response = await groupService.updateMembership(groupId, membership.id, membershipData);
        console.log('Partnership updated successfully:', {
          id: response.data.id,
          name: response.data.name
        });
        
        // Call the callback function to notify parent component
        if (onMembershipUpdated) {
          onMembershipUpdated(response.data);
        }
      } else {
        // Create new partnership
        response = await groupService.createMembership(groupId, membershipData);
        console.log('Partnership created successfully:', {
          id: response.data.id,
          name: response.data.name
        });
        
        // Call the callback function to notify parent component
        if (onMembershipCreated) {
          onMembershipCreated(response.data);
        }
      }
      
      resetForm();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} partnership:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} partnership. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (formStep < totalSteps) {
      setFormStep(formStep + 1);
    }
  };

  const prevStep = () => {
    if (formStep > 1) {
      setFormStep(formStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          {formStep > 1 ? (
            <button className="back-button" onClick={prevStep}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </button>
          ) : (
            <div>{/* Empty div for spacing */}</div>
          )}
          
          <h2 className="modal-title">{isEditMode ? 'Edit Partnership' : 'Create Partnership'}</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="event-form">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(formStep / totalSteps) * 100}%` }}
            ></div>
          </div>
          
          {formStep === 1 && (
            <div className="form-step">
              {/* Cover Image */}
              <div 
                className="cover-image-container" 
                onClick={() => document.getElementById('cover-image-input').click()}
                style={coverImagePreview ? {backgroundImage: `url(${coverImagePreview})`, backgroundColor: 'transparent'} : {}}
              >
                <input 
                  type="file" 
                  id="cover-image-input" 
                  name="coverImage"
                  onChange={handleFileChange} 
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                {!coverImagePreview && (
                  <>
                    <FontAwesomeIcon icon={faCamera} />
                    <span>Add cover image</span>
                  </>
                )}
                {coverImagePreview && (
                  <div className="image-overlay">
                    <span>Change image</span>
                  </div>
                )}
              </div>
              
              {/* What you're creating */}
              <div className="form-group">
                <label htmlFor="partnership-type">What are you creating?</label>
                <select 
                  id="partnership-type" 
                  className="form-control" 
                  defaultValue="partnership"
                  disabled
                >
                  <option value="partnership">Partnership</option>
                </select>
              </div>
              
              {/* Title */}
              <div className="form-group">
                <label htmlFor="title">Partnership Title</label>
                <input 
                  type="text" 
                  id="title" 
                  name="title" 
                  className="form-control" 
                  placeholder="E.g., Basic Partnership, Premium Partnership"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              {/* Description */}
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea 
                  id="description" 
                  name="description" 
                  className="form-control" 
                  placeholder="This is the description partners will see"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-next" 
                  onClick={nextStep}
                  disabled={!formData.title || !formData.description}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {formStep === 2 && (
            <div className="form-step">
              {/* Price with interval toggle */}
              <div className="form-group">
                <label htmlFor="price">Partnership Price</label>
                <div className="price-input-container" style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flexGrow: 1 }}>
                    <span style={{ position: 'absolute', left: '12px', top: '12px' }}>$</span>
                    <input 
                      type="number" 
                      id="price" 
                      name="price" 
                      className="form-control" 
                      placeholder="0.00"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      style={{ paddingLeft: '30px' }}
                      required
                    />
                  </div>
                  <div style={{ marginLeft: '10px' }}>
                    <select
                      id="interval"
                      name="interval"
                      className="form-control"
                      value={formData.interval}
                      onChange={handleInputChange}
                    >
                      <option value="monthly">per month</option>
                      <option value="annually">per year</option>
                      <option value="one-time">one-time</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Benefits/Rewards */}
              <div className="form-group">
                <label>Partnership Benefits (up to 3)</label>
                {formData.benefits.map((benefit, index) => (
                  <div key={index} style={{ marginBottom: '10px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={`Benefit ${index + 1}`}
                      value={benefit}
                      onChange={(e) => handleBenefitChange(index, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              
              {/* Thank You Note */}
              <div className="form-group">
                <label htmlFor="thankYouNote">Thank You Note</label>
                <textarea
                  id="thankYouNote"
                  name="thankYouNote"
                  className="form-control"
                  placeholder="This message will be shown to partners after they subscribe"
                  value={formData.thankYouNote}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-submit" 
                  disabled={isLoading || !formData.price}
                >
                  {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Partnership' : 'Create Partnership')}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateMembershipModal;